import DescriptionIcon from "@mui/icons-material/Description";
import FolderZipIcon from "@mui/icons-material/FolderCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, api, zapiszPlik } from "../api/client";
import type { Contract, ContractCandidate, ContractsView } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { formatMoney, sumMoney } from "../lib/money";
import { ile } from "../lib/polski";

/**
 * Umowy produkcji: kto ma, kto czeka i dlaczego jeszcze nie.
 *
 * **Dwie tabele, nie jedna.** Lista samych wystawionych umów mówi „wszystko
 * gotowe" także wtedy, gdy połowa obsady czeka na dane — a to jest dokładnie
 * ten stan, który wychodzi na jaw rano w dniu 1. Dlatego oczekujący na umowę
 * stoją na górze, z powodem przy każdym nazwisku.
 *
 * **Poprawka nie edytuje umowy** (§4.5). Dokument mógł już pojechać do
 * wykonawcy albo leżeć wydrukowany w teczce, więc „poprawiona wersja o tym
 * samym numerze" to dwa różne dokumenty pod jednym numerem. Stąd przycisk
 * mówi „Anuluj i wystaw nową", a nie „Edytuj".
 *
 * **Wystawienie umowy to nie oglądanie danych, ale jej wydruk już tak.**
 * Kierownik produkcji inicjuje umowy i widzi numer, kwotę i status; dokument
 * niesie PESEL, adres i rachunek, więc pobranie pliku wymaga uprawnienia
 * do danych wrażliwych (§6.4, §6.5). Przycisku wtedy nie ma — i ekran mówi
 * wprost dlaczego, bo brak przycisku bez wyjaśnienia wygląda na usterkę.
 */

const STAN: Record<string, "default" | "info" | "success" | "error"> = {
  wygenerowana: "info",
  podpisana: "success",
  anulowana: "error",
  przekazana: "default",
};

/** Powody, dla których umowy nie da się jeszcze wystawić — po ludzku. */
const PRZYCZYNA: Record<string, string> = {
  no_template: "Nie ma wzoru umowy dla tej formy współpracy",
  incomplete: "Wykonawca nie uzupełnił danych",
  no_amount: "Zaangażowanie nie ma kwoty",
  no_form: "Nie ustalono formy współpracy",
  cancelled: "Zaangażowanie anulowane",
  already_generated: "Umowa już istnieje",
};

export function ContractsPage() {
  const { code = "" } = useParams();
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [decyzja, setDecyzja] = useState<{ umowa: Contract; tryb: Tryb } | null>(null);
  const [hurt, setHurt] = useState<string>("");

  const { data } = useQuery({
    queryKey: ["contracts", code],
    queryFn: () => api.get<ContractsView>(`/projects/${code}/contracts`),
  });

  function odswiez() {
    kolejka.invalidateQueries({ queryKey: ["contracts", code] });
    // Wystawienie umowy przestawia stan zaangażowania, więc obsada
    // pokazywałaby nieaktualny stan do czasu przeładowania strony.
    kolejka.invalidateQueries({ queryKey: ["engagements", code] });
  }

  const wystaw = useMutation({
    mutationFn: (id: string) => api.post<Contract>(`/engagements/${id}/contract`, {}),
    onSuccess: () => { setBlad(null); odswiez(); },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const rozstrzygnij = useMutation({
    mutationFn: ({ umowa, tryb, reason }: { umowa: Contract; tryb: Tryb; reason: string }) =>
      api.post<Contract>(`/contracts/${umowa.id}/${tryb}`, { reason }),
    onSuccess: () => { setBlad(null); setDecyzja(null); odswiez(); },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  /**
   * Wystawienie hurtem — to jest właściwa czynność przed dniem 1.
   *
   * Żądania idą **po kolei, nie równolegle**: każde rezerwuje numer w tej
   * samej serii, a równoległe czekałyby na tej samej blokadzie wiersza
   * licznika. Kolejno jest tu więc szybciej, a przy okazji numery wychodzą
   * w kolejności listy zamiast losowej.
   */
  const wszystkie = useMutation({
    mutationFn: async (kandydaci: ContractCandidate[]) => {
      let wystawione = 0;
      const bledy: string[] = [];
      for (const k of kandydaci) {
        try {
          await api.post<Contract>(`/engagements/${k.id}/contract`, {});
          wystawione += 1;
        } catch (e) {
          bledy.push(`${k.person_label}: ${e instanceof ApiError ? e.message : "błąd"}`);
        }
      }
      return { wystawione, bledy };
    },
    onSuccess: ({ wystawione, bledy }) => {
      setBlad(null);
      setHurt(
        bledy.length === 0
          ? `Wystawiono ${ile(wystawione, "umowę", "umowy", "umów")}.`
          : `Wystawiono ${ile(wystawione, "umowę", "umowy", "umów")}, `
            + `nie udało się ${bledy.length}: ${bledy.join("; ")}`,
      );
      odswiez();
    },
  });

  /**
   * Teczka dnia 1 — jeden plik do druku.
   *
   * Pobieramy przez `fetch`, a nie zwykłym odsyłaczem, bo składanie
   * kilkudziesięciu umów trwa kilkanaście sekund. Przy odsyłaczu użytkownik
   * zostaje z pustą kartą i nie wie, czy coś się dzieje; tutaj widzi
   * oczekiwanie, a potem — ile dokumentów naprawdę powstało.
   */
  const teczka = useMutation({
    mutationFn: () => api.getFile(`/projects/${code}/contracts/folder`),
    onSuccess: ({ blob, filename, headers }) => {
      zapiszPlik(blob, filename);
      setBlad(null);
      const liczba = (nazwa: string) => Number(headers.get(`X-Teczka-${nazwa}`) ?? 0);
      const anulowanych = liczba("Cancelled");
      setHurt(
        `Teczka gotowa: ${ile(liczba("Contracts"), "umowa", "umowy", "umów")}`
        + ` w ${liczba("Copies")} egz., razem `
        + `${ile(liczba("Total-Pages"), "strona", "strony", "stron")} do druku.`
        + (anulowanych > 0
          ? ` Na liście kontrolnej ${ile(anulowanych, "umowa anulowana",
              "umowy anulowane", "umów anulowanych")} — do niepodpisywania.`
          : ""),
      );
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  if (!data) return null;

  const umowy = data.contracts;
  const czekajacy = data.candidates;
  const gotowi = czekajacy.filter((k) => k.ready);
  const czynne = umowy.filter((u) => u.status !== "anulowana");
  const brakWzorow = czekajacy.length > 0 && czekajacy.every((k) => k.code === "no_template");

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">{code} · umowy</Typography>
        <Box sx={{ flex: 1 }} />
        {data.can_download && czynne.length > 0 && (
          <Tooltip title="Jeden PDF do druku: lista kontrolna, przekładka przed
                          każdą umową i dwa egzemplarze każdego kompletu,
                          alfabetycznie po nazwisku.">
            <span>
              <Button variant="outlined" disabled={teczka.isPending}
                      startIcon={teczka.isPending
                        ? <CircularProgress size={16} />
                        : <FolderZipIcon />}
                      onClick={() => teczka.mutate()}>
                {teczka.isPending ? "Składam teczkę…" : "Teczka dnia 1"}
              </Button>
            </span>
          </Tooltip>
        )}
        {/* Skany są następnym krokiem po teczce, więc odsyłacz stoi obok. */}
        <Button component={Link} to={`/projekty/${code}/skany`} variant="outlined">
          Skany
        </Button>
        {can("contract.generate") && gotowi.length > 1 && (
          <Button variant="contained" startIcon={<DescriptionIcon />}
                  disabled={wszystkie.isPending}
                  onClick={() => wszystkie.mutate(gotowi)}>
            Wystaw wszystkie gotowe ({gotowi.length})
          </Button>
        )}
      </Stack>

      {blad && (
        <Alert severity="error" onClose={() => setBlad(null)}>
          {blad.message}
          {blad.body.problems.length > 0 && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </Alert>
      )}

      {hurt && <Alert severity="info" onClose={() => setHurt("")}>{hurt}</Alert>}

      {brakWzorow && (
        <Alert severity="warning">
          Nie ma wgranego wzoru umowy, więc nie da się wystawić żadnej.
          {can("admin.manage_config")
            ? <> Wgraj go w <Link to="/szablony">szablonach umów</Link>.</>
            : " Poproś administratora o wgranie szablonu."}
        </Alert>
      )}

      {czynne.length > 0 && !data.can_download && (
        <Alert severity="info">
          Umowy widzisz, ale nie pobierzesz: dokument niesie numer PESEL, adres
          i rachunek wykonawcy. Wydruk teczki dnia 1 składa osoba z dostępem
          do danych wrażliwych — zwykle księgowość.
        </Alert>
      )}

      {/* Czekający na górze: to jest lista rzeczy do zrobienia, a umowy
          wystawione są już tylko zapisem stanu. */}
      {czekajacy.length > 0 && (
        <Paper variant="outlined">
          <Stack direction="row" alignItems="center" spacing={1}
                 sx={{ px: 2, py: 1.25, bgcolor: "action.hover" }}>
            <Typography variant="subtitle2">
              Czeka na umowę: {czekajacy.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · gotowych {gotowi.length} ·{" "}
              {formatMoney(sumMoney(czekajacy.map((k) => k.amount_gross)))} zł brutto
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Osoba</TableCell>
                <TableCell>Rola</TableCell>
                <TableCell>Forma</TableCell>
                <TableCell align="right">Brutto</TableCell>
                <TableCell>Stan</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {czekajacy.map((k) => (
                <TableRow key={k.id} hover>
                  <TableCell>{k.person_label}</TableCell>
                  <TableCell>{k.role_name}</TableCell>
                  <TableCell>{k.form_label}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatMoney(k.amount_gross)}
                  </TableCell>
                  <TableCell>
                    {k.ready ? (
                      <Chip size="small" color="success" variant="outlined"
                            label="Gotowe" />
                    ) : (
                      <Tooltip title={k.problems.join(" · ")}>
                        <Chip size="small" color="warning" variant="outlined"
                              label={PRZYCZYNA[k.code] ?? "Nie można wystawić"} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {can("contract.generate") && (
                      <Button size="small" variant={k.ready ? "contained" : "text"}
                              disabled={!k.ready || wystaw.isPending}
                              onClick={() => wystaw.mutate(k.id)}>
                        Wystaw umowę
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper variant="outlined">
        <Stack direction="row" alignItems="center" spacing={1}
               sx={{ px: 2, py: 1.25, bgcolor: "action.hover" }}>
          <Typography variant="subtitle2">Wystawione umowy: {czynne.length}</Typography>
          <Typography variant="body2" color="text.secondary">
            · {formatMoney(sumMoney(czynne.map((u) => u.amount_gross)))} zł brutto
          </Typography>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numer</TableCell>
              <TableCell>Osoba</TableCell>
              <TableCell>Rola</TableCell>
              <TableCell>Forma</TableCell>
              <TableCell align="right">Brutto</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">
                <Tooltip title="Liczba stron wydruku, zapisywana przy pierwszym
                                złożeniu dokumentu. Posłuży do kontroli przy
                                skanowaniu podpisanych umów.">
                  <span>Stron</span>
                </Tooltip>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {umowy.map((u) => {
              const anulowana = u.status === "anulowana";
              return (
                <TableRow key={u.id} hover sx={{ opacity: anulowana ? 0.6 : 1 }}>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {u.number}
                    {u.supersedes_number && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        w miejsce {u.supersedes_number}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{u.person_label}</TableCell>
                  <TableCell>{u.role_name}</TableCell>
                  <TableCell>{u.form_label}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatMoney(u.amount_gross)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={u.status_label}
                          color={STAN[u.status] ?? "default"} />
                    {anulowana && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {u.cancel_reason}
                        {u.superseded_by_number && ` → ${u.superseded_by_number}`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{u.expected_pages ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {data.can_download && (
                        <Tooltip title="Dokument składany na żądanie z zamrożonej migawki">
                          {/* Zwykły odsyłacz, nie żądanie z kodu: przeglądarka
                              ma otworzyć PDF we własnej przeglądarce plików. */}
                          <Button size="small" startIcon={<PictureAsPdfIcon />}
                                  href={`/api/contracts/${u.id}/pdf`} target="_blank"
                                  rel="noopener">
                            Dokument
                          </Button>
                        </Tooltip>
                      )}
                      {can("contract.generate") && !anulowana && (
                        <>
                          <Button size="small"
                                  onClick={() => setDecyzja({ umowa: u, tryb: "regenerate" })}>
                            Popraw
                          </Button>
                          <Button size="small" color="error"
                                  onClick={() => setDecyzja({ umowa: u, tryb: "cancel" })}>
                            Anuluj
                          </Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {umowy.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Żadna umowa nie została jeszcze wystawiona.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <PowodDialog
        stan={decyzja}
        pracuje={rozstrzygnij.isPending}
        blad={blad?.message}
        onClose={() => { setDecyzja(null); setBlad(null); }}
        onConfirm={(reason) => decyzja && rozstrzygnij.mutate({ ...decyzja, reason })}
      />
    </Stack>
  );
}

type Tryb = "cancel" | "regenerate";

function PowodDialog({ stan, pracuje, blad, onClose, onConfirm }: {
  stan: { umowa: Contract; tryb: Tryb } | null;
  pracuje: boolean;
  blad?: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [powod, setPowod] = useState("");
  const poprawka = stan?.tryb === "regenerate";

  return (
    <Dialog open={stan !== null} onClose={onClose} fullWidth maxWidth="sm"
            // Pole czyścimy przy otwarciu, nie przy zamknięciu: po błędzie
            // serwera okno zostaje otwarte i wpisany powód ma w nim zostać.
            TransitionProps={{ onEnter: () => setPowod("") }}>
      <DialogTitle>
        {poprawka ? "Anuluj i wystaw nową" : "Anuluj umowę"} {stan?.umowa.number}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {poprawka
            ? "Nowa umowa dostanie nowy numer, a ta zostanie anulowana ze wskazaniem "
              + "następcy. Poprawka nie zmienia dokumentu, bo mógł już pojechać "
              + "do wykonawcy albo leżeć w teczce dnia 1."
            : "Anulowana umowa zostaje na liście kontrolnej dnia 1 — prawnik musi "
              + "wiedzieć, dlaczego trzyma w ręku dokument, którego nie wolno podpisać."}
        </DialogContentText>
        {blad && <Alert severity="error" sx={{ mb: 2 }}>{blad}</Alert>}
        <TextField
          autoFocus fullWidth multiline minRows={2} value={powod}
          label="Powód (wymagany)"
          helperText="Trafi na listę kontrolną dnia 1."
          onChange={(e) => setPowod(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
        <Button variant="contained" color={poprawka ? "primary" : "error"}
                disabled={pracuje || !powod.trim()}
                onClick={() => onConfirm(powod.trim())}>
          {poprawka ? "Anuluj i wystaw nową" : "Anuluj umowę"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
